/**
 * Tax-regime engine — pure functions, no DOM, no React.
 *
 * Faithful port of the JS prototype in `finmin_tool1_enhanced.html`. The math
 * is anchored to EY FAQ May 2026 hand-computed cases for FY 2026-27 (see the
 * tests in the prototype's `runEngineTests`). Don't change the algorithms
 * here without re-validating against those numbers.
 */
import type {
  Rules, RegimeRules, RebateConfig, SlabBand, SurchargeBand,
} from './rules';
import type {
  ProfileInput, RegimeResult, AgeBand, AdvanceTaxSchedule, Opportunity,
} from './types';
import { fmt } from './format';

// ── Slab tax (progressive marginal rates) ───────────────────────────────────
export function applySlabs(taxableIncome: number, slabs: SlabBand[]): number {
  if (taxableIncome <= 0) return 0;
  let tax = 0;
  let prev = 0;
  for (const slab of slabs) {
    if (taxableIncome <= prev) break;
    const top = Math.min(taxableIncome, slab.upTo);
    tax += (top - prev) * slab.rate;
    prev = slab.upTo;
  }
  return tax;
}

export function getOldRegimeSlabs(age: AgeBand, rules: Rules): SlabBand[] {
  const old = rules.oldRegime;
  if (age === 'above80') return old.slabsAbove80!;
  if (age === '60to80')  return old.slabs60to80!;
  return old.slabsBelow60!;
}

// ── 87A rebate (with marginal relief in the new regime) ────────────────────
export function apply87ARebate(
  slabTax:        number,
  taxableIncome:  number,
  rebateConfig:   RebateConfig,
): number {
  const { thresholdTI, maxRebate, marginalRelief } = rebateConfig;
  if (taxableIncome <= thresholdTI) {
    return Math.min(slabTax, maxRebate);
  }
  if (marginalRelief) {
    // At TI just above threshold, post-rebate tax must not exceed
    // the income above the threshold.
    const reliefRebate = slabTax - (taxableIncome - thresholdTI);
    return Math.max(0, Math.min(reliefRebate, slabTax));
  }
  return 0;
}

// ── Surcharge (rate + marginal relief) ─────────────────────────────────────
export function getSurchargeRate(
  taxableIncome: number,
  surchargeBands: SurchargeBand[],
): number {
  let rate = 0;
  for (const band of surchargeBands) {
    if (taxableIncome > band.exceeds) rate = band.rate;
  }
  return rate;
}

export function applySurcharge(
  taxAfterRebate:   number,
  taxableIncome:    number,
  surchargeBands:   SurchargeBand[],
  taxAtThresholdFn: (threshold: number) => number,
): { surcharge: number; rate: number; reliefApplied: boolean } {
  const rate = getSurchargeRate(taxableIncome, surchargeBands);
  if (rate === 0) return { surcharge: 0, rate: 0, reliefApplied: false };

  let raw = taxAfterRebate * rate;

  let crossedThreshold = 0;
  let prevRate = 0;
  for (let i = 0; i < surchargeBands.length; i++) {
    if (taxableIncome > surchargeBands[i]!.exceeds) {
      crossedThreshold = surchargeBands[i]!.exceeds;
      prevRate = i > 0 ? surchargeBands[i - 1]!.rate : 0;
    }
  }

  const taxAtThreshold = taxAtThresholdFn(crossedThreshold);
  const cap = taxAtThreshold * (1 + prevRate) + (taxableIncome - crossedThreshold);
  let reliefApplied = false;
  if (taxAfterRebate + raw > cap) {
    raw = Math.max(0, cap - taxAfterRebate);
    reliefApplied = true;
  }
  return { surcharge: raw, rate, reliefApplied };
}

export function applyCess(taxPlusSurcharge: number, cessRate: number): number {
  return taxPlusSurcharge * cessRate;
}

// ── Capital gains (special-rate heads) ─────────────────────────────────────
export function applyCapitalGainsTax(
  profile: ProfileInput,
  cgRules: Rules['capitalGains'],
) {
  const ltcgEqTaxable = Math.max(0, (profile.ltcgEquity || 0) - cgRules.ltcgEquity.exemption);
  const ltcgEqTax = ltcgEqTaxable * cgRules.ltcgEquity.rate;
  const stcgEqTax = (profile.stcgEquity     || 0) * cgRules.stcgEquity.rate;
  const ltcgDebtTax = (profile.ltcgDebt     || 0) * cgRules.ltcgDebt.rate;
  const ltcgReTax = (profile.ltcgRealEstate || 0) * cgRules.ltcgRealEstate.rate;
  return {
    taxableLtcgEq: ltcgEqTaxable,
    ltcgEqTax, stcgEqTax, ltcgDebtTax, ltcgReTax,
    total: ltcgEqTax + stcgEqTax + ltcgDebtTax + ltcgReTax,
  };
}

export function isMetroCity(cityName: string | null | undefined, rules: Rules): boolean {
  if (!cityName) return false;
  const lower = String(cityName).trim().toLowerCase();
  return rules.hraMetroCities.some((c) => c.toLowerCase() === lower);
}

// ── Core: regime tax computation ───────────────────────────────────────────
export function computeRegimeTax(
  profile: ProfileInput,
  regime:  'old' | 'new',
  rules:   Rules,
): RegimeResult {
  const cfg: RegimeRules = regime === 'new' ? rules.newRegime : rules.oldRegime;
  const slabs = regime === 'new' ? cfg.slabs! : getOldRegimeSlabs(profile.age, rules);

  const gross = profile.gross || 0;
  const basic = profile.basic || 0;
  const empNps = Math.round((profile.employerNpsPct || 0) / 100 * basic);
  const bothPerks = empNps
    + (profile.mealVouchers     || 0)
    + (profile.mobileReimb      || 0)
    + (profile.uniformAllowance || 0);

  let oldOnly = 0;
  let hraExemption = 0;
  if (regime === 'old') {
    if ((profile.hraReceived || 0) > 0 && (profile.rentPaid || 0) > 0) {
      const rentMinus10pctBasic = Math.max(0, profile.rentPaid - 0.10 * basic);
      const pctOfBasic = (profile.isMetro ? 0.50 : 0.40) * basic;
      hraExemption = Math.min(profile.hraReceived, rentMinus10pctBasic, pctOfBasic);
    }
    oldOnly += hraExemption;
    oldOnly += Math.min(profile.homeLoanInterest || 0, 200000);
    oldOnly += Math.min(profile.s80c             || 0, 150000);
    oldOnly += Math.min(profile.nps80ccd1b       || 0, 50000);

    const selfCap    = profile.s80dSelfSenior    ? 50000 : 25000;
    oldOnly += Math.min(profile.s80dSelf || 0, selfCap);
    const parentsCap = profile.s80dParentsSenior ? 50000 : 25000;
    oldOnly += Math.min(profile.s80dParents || 0, parentsCap);
    oldOnly += profile.eduLoanInterest || 0;
  }

  const otherSlabIncome = (profile.fdInterest      || 0)
                        + (profile.savingsInterest || 0)
                        + (profile.stcgDebt        || 0);

  const stdDed = cfg.standardDeduction;
  const ti = Math.max(0, gross - bothPerks - stdDed - oldOnly + otherSlabIncome);

  const slabTax = applySlabs(ti, slabs);
  const rebate  = apply87ARebate(slabTax, ti, cfg.rebate87a);
  const taxAR   = Math.max(0, slabTax - rebate);

  const taxAtThresholdFn = (thr: number) => {
    const t = applySlabs(thr, slabs);
    const r = apply87ARebate(t, thr, cfg.rebate87a);
    return Math.max(0, t - r);
  };
  const sc = applySurcharge(taxAR, ti, cfg.surcharge, taxAtThresholdFn);

  const cg = applyCapitalGainsTax(profile, rules.capitalGains);
  const cgSurchargeRate = Math.min(sc.rate, rules.capitalGains.surchargeCap);
  const cgSurcharge = cg.total * cgSurchargeRate;

  const taxBeforeCess = taxAR + sc.surcharge + cg.total + cgSurcharge;
  const cess = applyCess(taxBeforeCess, rules.cess);
  const total = Math.round(taxBeforeCess + cess);

  return {
    regime,
    taxableIncome: ti,
    deductions: { stdDed, hraExemption, oldOnly, bothPerks, empNps },
    slabTax:        Math.round(slabTax),
    rebate:         Math.round(rebate),
    taxAfterRebate: Math.round(taxAR),
    surcharge:      Math.round(sc.surcharge),
    surchargeRate:  sc.rate,
    surchargeReliefApplied: sc.reliefApplied,
    capitalGains: {
      ...cg,
      surcharge:     Math.round(cgSurcharge),
      surchargeRate: cgSurchargeRate,
    },
    cess:  Math.round(cess),
    total,
  };
}

// ── Effective marginal rate per ₹ of additional deduction ──────────────────
export function getEffectiveMarginalRate(
  profile: ProfileInput,
  regime:  'old' | 'new',
  result:  RegimeResult,
  rules:   Rules,
): number {
  const cfg: RegimeRules = regime === 'new' ? rules.newRegime : rules.oldRegime;
  const slabs = regime === 'new' ? cfg.slabs! : getOldRegimeSlabs(profile.age, rules);
  const ti = result.taxableIncome;

  let slabRate = 0;
  let prev = 0;
  for (const s of slabs) {
    if (ti <= s.upTo) { slabRate = s.rate; break; }
    prev = s.upTo;
  }
  if (ti > slabs[slabs.length - 2]!.upTo) slabRate = slabs[slabs.length - 1]!.rate;
  void prev;

  if (cfg.rebate87a.marginalRelief && ti > cfg.rebate87a.thresholdTI && result.rebate > 0) {
    return 1.00 * (1 + rules.cess); // ~104% inside the marginal-relief zone
  }
  return slabRate * (1 + result.surchargeRate) * (1 + rules.cess);
}

// ── Optimizer (tax-saving opportunities) ───────────────────────────────────
export function computeOpportunities(
  profile: ProfileInput,
  regime:  'old' | 'new',
  result:  RegimeResult,
  rules:   Rules,
): Opportunity[] {
  const margRate = getEffectiveMarginalRate(profile, regime, result, rules);
  const opps: Omit<Opportunity, 'rank'>[] = [];

  // 80C (old regime)
  if (regime === 'old') {
    const cap = 150000;
    const used = Math.min(profile.s80c || 0, cap);
    const gap = cap - used;
    if (gap >= 5000) opps.push({
      title:  'Top up Section 80C by ' + fmt(gap),
      delta:  gap,
      impact: gap * margRate,
      sec:    'S.123',
      ease:   'easy',
      caveat: 'Currently using ' + fmt(used) + ' of ' + fmt(cap) + ' limit. ELSS (3-yr lock-in), PPF top-up or EPF VPF.',
    });
  }

  // NPS Tier-1 own contribution (old regime)
  if (regime === 'old') {
    const cap = 50000;
    const used = Math.min(profile.nps80ccd1b || 0, cap);
    const gap = cap - used;
    if (gap >= 5000) opps.push({
      title:  'Add NPS Tier-1 own contribution of ' + fmt(gap),
      delta:  gap,
      impact: gap * margRate,
      sec:    'S.124(3)',
      ease:   'easy',
      caveat: 'Over and above the ₹1.5L 80C cap. Lock-in until 60. Suitable if retirement-saving aligns.',
    });
  }

  // Employer NPS — both regimes, very high leverage
  const empNpsPct = profile.employerNpsPct || 0;
  if (empNpsPct < 10 && profile.basic > 0) {
    const additional = Math.round((10 - empNpsPct) / 100 * profile.basic);
    opps.push({
      title:  'Restructure CTC to add employer NPS at 10% of basic',
      delta:  additional,
      impact: additional * margRate,
      sec:    'S.124(1)',
      ease:   'struct',
      caveat: 'Available in BOTH regimes. ' + fmt(additional) + ' redirected from existing CTC. Negotiate with HR before April.',
    });
  }

  // 80D parents (old regime)
  if (regime === 'old') {
    const cap = profile.s80dParentsSenior ? 50000 : 25000;
    const used = Math.min(profile.s80dParents || 0, cap);
    const gap = cap - used;
    if (gap >= 5000) opps.push({
      title:  'Health insurance for parents — add ' + fmt(gap),
      delta:  gap,
      impact: gap * margRate,
      sec:    'S.126',
      ease:   'easy',
      caveat: profile.s80dParentsSenior
        ? 'Senior-citizen cap ₹50K. Genuinely useful coverage, not just tax-driven.'
        : 'Cap ₹25K (₹50K once parents turn 60).',
    });
  }

  // 80D self/family (old regime)
  if (regime === 'old') {
    const cap = profile.s80dSelfSenior ? 50000 : 25000;
    const used = Math.min(profile.s80dSelf || 0, cap);
    const gap = cap - used;
    if (gap >= 5000) opps.push({
      title:  'Health insurance for self & family — add ' + fmt(gap),
      delta:  gap,
      impact: gap * margRate,
      sec:    'S.126',
      ease:   'easy',
      caveat: 'Cap ' + fmt(cap) + '. Many salaried have employer coverage but no personal policy — gap risk if you change jobs.',
    });
  }

  // Meal vouchers — both regimes, easy CTC tweak
  if ((profile.mealVouchers || 0) < 13200) {
    const gap = 13200 - (profile.mealVouchers || 0);
    opps.push({
      title:  'Add meal vouchers — ' + fmt(gap) + '/yr',
      delta:  gap,
      impact: gap * margRate,
      sec:    'S.17(2)(e)',
      ease:   'struct',
      caveat: 'Fully exempt in both regimes. ₹50/meal × 22 working days × 12 months ≈ ₹13,200.',
    });
  }

  opps.sort((a, b) => b.impact - a.impact);
  return opps.slice(0, 6).map((o, i) => ({ ...o, rank: i + 1 }));
}

// ── Advance-tax obligation (Sec 208/211) ───────────────────────────────────
export function computeAdvanceTax(annualTax: number): AdvanceTaxSchedule | null {
  if (annualTax * 0.9 < 10000) return null;
  const q1 = Math.round(annualTax * 0.15);
  const q2 = Math.round(annualTax * 0.45) - q1;
  const q3 = Math.round(annualTax * 0.75) - q1 - q2;
  const q4 = annualTax - q1 - q2 - q3;
  return {
    total: annualTax,
    q1: { amount: q1, dueDate: '15 Jun', cumulative: q1 },
    q2: { amount: q2, dueDate: '15 Sep', cumulative: q1 + q2 },
    q3: { amount: q3, dueDate: '15 Dec', cumulative: q1 + q2 + q3 },
    q4: { amount: q4, dueDate: '15 Mar', cumulative: annualTax },
  };
}
